import React, { useState, useEffect, useCallback } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import "./login.css";
import packageInfo from "../../package.json";
import { useNavigate } from 'react-router-dom';

function getFriendlyFirebaseError(error) {
  if (!error || !error.code) return "An unexpected error occurred. Please try again.";
  switch (error.code) {
    case "auth/email-already-in-use": return "That email is already registered.";
    case "auth/invalid-email": return "Please enter a valid email address.";
    case "auth/user-not-found": return "No account found with that email address.";
    case "auth/wrong-password": return "Incorrect password. Please try again.";
    default: return error.message || "An unexpected error occurred.";
  }
}

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [referrer, setReferrer] = useState("");
  const [mode, setMode] = useState("login");
  const [showEULA, setShowEULA] = useState(false);
  const [eulaText, setEulaText] = useState("");
  const [acceptedEULA, setAcceptedEULA] = useState(false);
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isProcessingReferral, setIsProcessingReferral] = useState(false);
  const navigate = useNavigate();

const handleReferral = useCallback(async (user, code) => {
  if (isProcessingReferral || !code) return;
  setIsProcessingReferral(true);

  try {
    const idToken = await user.getIdToken();

    const response = await fetch("https://referral-5dhfwyjvzq-uc.a.run.app", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${idToken}`
      },
      body: JSON.stringify({ referralCode: code.trim() })
    });

    const data = await response.json();

    sessionStorage.removeItem("pendingReferralCode");
    localStorage.removeItem("pendingReferralCode");

    if (response.ok) {

      window.location.href = "/referral-activated.html";
    } else {

      const reason = data.error || "invalid";
      navigate(`/referral-error?reason=${encodeURIComponent(reason)}`);
    }
  } catch (fetchErr) {
    console.error("Referral fetch failed:", fetchErr);

    navigate('/referral-error?reason=system_error');
  } finally {
    setIsProcessingReferral(false);
  }
}, [isProcessingReferral, navigate]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const params = new URLSearchParams(window.location.search);

      const urlCode = params.get("code") || params.get("ref");
      const savedCode = localStorage.getItem("pendingReferralCode");
      
      const codeToUse = urlCode || savedCode;

      if (codeToUse) {
        setReferrer(codeToUse);
        localStorage.setItem("pendingReferralCode", codeToUse);

        if (window.location.search) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }


        if (user && !isProcessingReferral) {
          handleReferral(user, codeToUse);
        } else if (!user) {

          setMode("create");
        }
      }
    });

    return () => unsubscribe();
  }, [handleReferral, isProcessingReferral]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setSuccessMsg("");

    if (mode === "forgot") {
      try {
        await sendPasswordResetEmail(auth, email);
        setSuccessMsg("RESET LINK SENT: Check your email to proceed.");
      } catch (error) {
        setFormError(getFriendlyFirebaseError(error));
      }
      return;
    }

    try {
      let userCredential;
      if (mode === "login") {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      } else {
        if (!acceptedEULA) {
          setFormError("You must accept the EULA to continue.");
          return;
        }
        if (password !== repeatPassword) {
          setFormError("Passwords do not match.");
          return;
        }
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
      }

      const user = userCredential.user;

      if (mode === "login") {
        const userDocRef = doc(db, "Users", user.uid);
        const userSnap = await getDoc(userDocRef);
        
        if (!userSnap.exists()) {
          setFormError("User profile not found.");
          await signOut(auth);
          return;
        }

        const userData = userSnap.data();
        const bannedSnap = await getDoc(doc(db, "Global_Vars", "Banned_Players"));
        if (bannedSnap.exists() && userData.userDisplayName) {
          const banReason = bannedSnap.data()[userData.userDisplayName];
          if (banReason) {
            setFormError(`You are banned: ${banReason}`);
            await signOut(auth);
            return;
          }
        }
      }

      const finalCode = referrer.trim() || localStorage.getItem("pendingReferralCode");
      if (finalCode) {
        await handleReferral(user, finalCode);
      } else {
        onLogin?.(user.uid);
      }

    } catch (error) {
      setFormError(getFriendlyFirebaseError(error));
    }
  };

  const openEULA = async () => {
    try {
      const res = await fetch("/eula.txt");
      const text = await res.text();
      setEulaText(text);
      setShowEULA(true);
    } catch (err) {
      setEulaText("Failed to load EULA.");
      setShowEULA(true);
    }
  };

  const toggleMode = (newMode) => {
    setMode(newMode);
    setFormError("");
    setSuccessMsg("");
  };

  return (
    <div className="login-wrapper">
      <div className="header-panel">OUTTRADE ONLINE</div>

      <form className="login-container" onSubmit={handleSubmit}>
        <h1>
          {mode === "login" && "Login"}
          {mode === "create" && "Create Account"}
          {mode === "forgot" && "Reset Password"}
        </h1>

        <input
          type="email"
          placeholder="Email"
          value={email}
          required
          onChange={(e) => setEmail(e.target.value)}
        />

        {mode !== "forgot" && (
          <input
            type="password"
            placeholder="Password"
            value={password}
            required
            onChange={(e) => setPassword(e.target.value)}
          />
        )}

        {mode === "create" && (
          <>
            <input
              type="password"
              placeholder="Repeat Password"
              value={repeatPassword}
              required
              onChange={(e) => setRepeatPassword(e.target.value)}
            />

            <input
              type="text"
              placeholder="Referrer Code"
              className={referrer ? "highlight-input" : ""}
              value={referrer}
              onChange={(e) => setReferrer(e.target.value)}
            />

            <div className="eula-section">
              <button type="button" className="link-button" onClick={openEULA}>
                View EULA
              </button>
              <label>
                <input
                  type="checkbox"
                  checked={acceptedEULA}
                  onChange={(e) => setAcceptedEULA(e.target.checked)}
                />{" "}
                I accept the EULA
              </label>
            </div>
          </>
        )}

        {formError && <div className="error-message">{formError}</div>}
        {successMsg && <div className="success-message" style={{ color: '#50e3c2', fontSize: '12px', margin: '10px 0' }}>{successMsg}</div>}

        <button type="submit" disabled={isProcessingReferral}>
          {isProcessingReferral ? "Processing..." : mode === "login" ? "Login" : mode === "create" ? "Sign Up" : "Send Reset Link"}
        </button>

        <div className="mode-toggle-group">
          {mode === "login" ? (
            <>
              <p onClick={() => toggleMode("create")}>Don't have an account? Create one</p>
              <p onClick={() => toggleMode("forgot")} style={{ marginTop: '10px', fontSize: '11px', opacity: 0.7 }}>Forgot password?</p>
            </>
          ) : (
            <p onClick={() => toggleMode("login")}>Back to Login</p>
          )}
        </div>
      </form>

      {showEULA && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal-container">
            <div className="confirm-modal-title">End User License Agreement</div>
            <div className="eula-content">{eulaText}</div>
            <div className="confirm-modal-actions">
              <button className="settings-button decline" onClick={() => setShowEULA(false)}>Decline</button>
              <button className="settings-button accept" onClick={() => { setAcceptedEULA(true); setShowEULA(false); }}>Accept</button>
            </div>
          </div>
        </div>
      )}

      <p className="version-label">v{packageInfo.version}</p>
    </div>
  );
}